// Small presentation helpers shared across views.

import type { Cell, ParticipantRef, PlayerId, TournamentState } from '../domain';
import { groupMatchOrder } from '../domain';

/** Compact label for a player chip in matrices, e.g. "P12". */
export function playerShort(playerId: PlayerId): string {
  return playerId;
}

/**
 * Friendly "Match N" label for a group cell, numbered by play order so it lines
 * up with the printed score sheet. Falls back to a bare "Match" if the cell's
 * position can't be resolved. Pass the `t` from `useTranslation` to localise it;
 * without one it returns the English strings.
 */
export function groupMatchLabel(
  tournament: TournamentState,
  cell: Cell,
  t?: RefTranslator,
): string {
  const order = groupMatchOrder(tournament, cell);
  if (t) {
    return order > 0 ? t('admin.labels.matchN', { order }) : t('admin.labels.match');
  }
  return order > 0 ? `Match ${order}` : 'Match';
}

/** Full display name, falling back to the id. */
export function playerName(
  tournament: TournamentState,
  playerId: PlayerId | null,
): string {
  if (!playerId) return '';
  return tournament.players[playerId]?.name ?? playerId;
}

/** Optional translator for `describeRef`; matches `useTranslation().t`. */
export type RefTranslator = (
  key: string,
  params?: Record<string, string | number>,
) => string;

/**
 * Human description of a not-yet-resolved bracket participant. Without a
 * translator it returns the English strings (used by the admin view); pass the
 * `t` from `useTranslation` to localise it (used by the public view).
 */
export function describeRef(
  ref: ParticipantRef,
  tournament: TournamentState,
  t?: RefTranslator,
): string {
  if (ref.kind === 'slot') {
    const slot = tournament.slots[ref.slotId];
    if (!slot) return ref.slotId;
    const key = slot.position === 1 ? 'ref.group1st' : 'ref.group2nd';
    if (t) return t(key, { label: slot.poolLabel });
    return `Group ${slot.poolLabel} · ${slot.position === 1 ? '1st' : '2nd'}`;
  }
  if (ref.kind === 'winner') {
    const node = tournament.nodes[ref.nodeId];
    if (!node) return ref.nodeId;
    return t ? t('ref.winnerOf', { label: node.label }) : `Winner of ${node.label}`;
  }
  if (ref.kind === 'loser') {
    const node = tournament.nodes[ref.nodeId];
    if (!node) return ref.nodeId;
    return t ? t('ref.loserOf', { label: node.label }) : `Loser of ${node.label}`;
  }
  return t ? t('ref.bye') : 'Bye';
}

/**
 * Up-to-two-letter monogram for the account avatar in the app bar (see
 * `features/user-settings.md`). Uses the first letters of the first and last
 * word of the display name; falls back to the email's local part when the
 * account has no name yet (staff accounts are created with an empty one), and
 * to '?' when there is nothing at all. Word-based rather than character-sliced
 * so a non-Latin name yields its own initials rather than mojibake.
 */
export function accountInitials(name: string, email: string): string {
  const words = name.trim().split(/\s+/).filter((word) => word.length > 0);
  if (words.length > 0) {
    const first = [...words[0]][0] ?? '';
    const last = words.length > 1 ? ([...words[words.length - 1]][0] ?? '') : '';
    return (first + last).toUpperCase();
  }
  const local = email.trim().split('@')[0] ?? '';
  const letter = [...local][0] ?? '';
  return letter.length > 0 ? letter.toUpperCase() : '?';
}
